/**
 * Scene lighting: tonemap, exposure, contrast, saturation, bloom.
 *
 * These are viewer settings (scene.json `lighting`, read by viewer2's
 * apply-lighting.ts onto a pc.CameraFrame). The editor previews them on its
 * own pipeline: tonemap and exposure go through the engine camera exactly as
 * the viewer applies them; contrast, saturation and bloom are applied by the
 * final blit (camera.setGrade) on the display image, which is a close
 * preview rather than the viewer's pre-tonemap grade.
 *
 * State lives here (not on the camera) so it survives a camera rebuild, is
 * serialised into the sidecar, and resets with the scene.
 */

import type { Events } from '../events';
import type { Scene } from '../scene';

export type LightingSettings = {
    tonemap: 'linear' | 'filmic' | 'hejl' | 'aces' | 'aces2' | 'neutral';
    /** EV stops; 0 is neutral. */
    exposure: number;
    contrast: number;
    saturation: number;
    bloom: { enabled: boolean; intensity: number };
};

/** scene.json block (documentation/viewer/scene-schema.md → "Lighting"). */
export type LightingConfig = {
    tonemap: LightingSettings['tonemap'];
    exposure: number;
    contrast: number;
    bloom: { enabled: boolean; intensity: number };
    colorEnhance?: { enabled: boolean; vibrance: number };
};

export const LIGHTING_DEFAULTS: Readonly<LightingSettings> = Object.freeze({
    tonemap: 'linear',
    exposure: 0,
    contrast: 1,
    saturation: 1,
    bloom: Object.freeze({ enabled: false, intensity: 0.03 })
});

export const TONEMAP_NAMES: ReadonlyArray<LightingSettings['tonemap']> = ['linear', 'filmic', 'hejl', 'aces', 'aces2', 'neutral'];

const clone = (s: LightingSettings): LightingSettings => ({ ...s, bloom: { ...s.bloom } });
const round = (v: number, d = 3) => Math.round(v * 10 ** d) / 10 ** d;
const near = (a: number, b: number) => Math.abs(a - b) < 1e-4;

type GradeCamera = { setGrade?: (grade: { contrast: number; saturation: number; bloomIntensity: number }) => void };

const registerLighting = (events: Events, scene: Scene) => {
    let state = clone(LIGHTING_DEFAULTS);

    const isDefault = (s: LightingSettings) => s.tonemap === 'linear' && near(s.exposure, 0) &&
        near(s.contrast, 1) && near(s.saturation, 1) && !s.bloom.enabled;

    let applying = false;
    const apply = () => {
        applying = true;
        try {
            if (scene.camera.tonemapping !== state.tonemap) scene.camera.tonemapping = state.tonemap;
            scene.app.scene.exposure = 2 ** state.exposure;
            (scene.camera as unknown as GradeCamera).setGrade?.({
                contrast: state.contrast,
                saturation: state.saturation,
                bloomIntensity: state.bloom.enabled ? state.bloom.intensity : 0
            });
            scene.forceRender = true;
        } finally {
            applying = false;
        }
    };

    const set = (patch: Partial<LightingSettings> & { bloom?: Partial<LightingSettings['bloom']> }) => {
        if (!patch || typeof patch !== 'object') return;
        const next = clone(state);
        if (patch.tonemap && (TONEMAP_NAMES as readonly string[]).includes(patch.tonemap)) next.tonemap = patch.tonemap;
        if (typeof patch.exposure === 'number' && Number.isFinite(patch.exposure)) next.exposure = Math.max(-6, Math.min(6, patch.exposure));
        if (typeof patch.contrast === 'number' && Number.isFinite(patch.contrast)) next.contrast = Math.max(0, Math.min(3, patch.contrast));
        if (typeof patch.saturation === 'number' && Number.isFinite(patch.saturation)) next.saturation = Math.max(0, Math.min(3, patch.saturation));
        if (patch.bloom && typeof patch.bloom === 'object') {
            if (typeof patch.bloom.enabled === 'boolean') next.bloom.enabled = patch.bloom.enabled;
            if (typeof patch.bloom.intensity === 'number' && Number.isFinite(patch.bloom.intensity)) next.bloom.intensity = Math.max(0, Math.min(1, patch.bloom.intensity));
        }
        state = next;
        apply();
        events.fire('lighting.changed', clone(state));
    };

    events.function('lighting.get', () => clone(state));
    events.function('lighting.isDefault', () => isDefault(state));
    events.on('lighting.set', set);
    events.on('lighting.reset', () => set(clone(LIGHTING_DEFAULTS)));

    /** The scene.json block, or undefined when everything is neutral (viewer then uses its passthrough). */
    events.function('lighting.sceneConfig', (): LightingConfig | undefined => {
        if (isDefault(state)) return undefined;
        const cfg: LightingConfig = {
            tonemap: state.tonemap,
            exposure: round(state.exposure),
            contrast: round(state.contrast),
            bloom: { enabled: state.bloom.enabled, intensity: round(state.bloom.intensity) }
        };
        if (!near(state.saturation, 1)) {
            // viewer2 maps vibrance onto CameraFrame grading saturation (1 + vibrance)
            cfg.colorEnhance = { enabled: true, vibrance: round(state.saturation - 1) };
        }
        return cfg;
    });

    /** Load a scene.json / sidecar block (either shape). */
    events.on('lighting.load', (cfg: any) => {
        if (!cfg || typeof cfg !== 'object') return;
        const patch: any = { tonemap: cfg.tonemap, exposure: cfg.exposure, contrast: cfg.contrast, bloom: cfg.bloom };
        if (typeof cfg.saturation === 'number') patch.saturation = cfg.saturation;
        else if (cfg.colorEnhance?.enabled && typeof cfg.colorEnhance.vibrance === 'number') patch.saturation = 1 + cfg.colorEnhance.vibrance;
        set(patch);
    });

    // the settings panel's tonemap select also drives camera.tonemapping; keep in step
    events.on('camera.tonemapping', (value: string) => {
        if (applying) return;
        if ((TONEMAP_NAMES as readonly string[]).includes(value) && value !== state.tonemap) {
            state.tonemap = value as LightingSettings['tonemap'];
            events.fire('lighting.changed', clone(state));
        }
    });

    events.on('scene.clear', () => {
        state = clone(LIGHTING_DEFAULTS);
        apply();
        events.fire('lighting.changed', clone(state));
    });

    apply();
};

export { registerLighting };
